import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import InstructorAnalyticsService, { StudentDetails } from '../../services/instructorAnalyticsService';
import { getAvailableCourses } from '../../services/elearningCourseService';
import { 
  Users, 
  UserCheck, 
  Search, 
  Filter as FilterIcon, 
  MoreVertical, 
  Mail, 
  Calendar, 
  TrendingUp, 
  Award,
  BookOpen,
  Eye,
  MessageSquare,
  Download,
  Grid,
  List,
  Clock,
  Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

// L'interface Student est maintenant alignée sur StudentDetails du service
interface Student extends StudentDetails {}

interface StudentCourse {
  course_id: string;
  course_title: string;
  enrollment_date: string;
  progress: number;
  last_accessed: string;
  completion_date: string | null;
  grade: number | null;
  time_spent: number; // en minutes
}

const InstructorStudentsPage: React.FC = () => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { instructor, hasPermission, isLoading: authLoading } = useInstructorPermissions(['view_students']);
  
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('last_activity');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  // Cours réels de l'instructeur
  const [instructorCourses, setInstructorCourses] = useState<{id: string, title: string}[]>([]);
  const [studentsStats, setStudentsStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    completedCourses: 0,
    averageProgress: 0
  });

  // Libellés FR pour les triggers Select
  const STATUS_LABELS_FR: Record<string, string> = {
    all: 'Tous les statuts',
    active: 'Actif',
    inactive: 'Inactif',
    completed: 'Terminé',
  };

  const SORT_LABELS_FR: Record<string, string> = {
    last_activity: 'Dernière activité',
    name: 'Nom',
    enrollment_date: "Date d'inscription",
    progress: 'Progression',
    grade: 'Note moyenne',
  };

  useEffect(() => {
    if (tenant?.id && instructor?.id) {
      loadStudents();
      loadInstructorCourses();
    }
  }, [tenant, instructor, statusFilter, courseFilter, sortBy]);

    const loadStudents = async () => {
    try {
      setLoading(true);
      
      if (!tenant?.id || !instructor?.id) {
        setLoading(false);
        return;
      }

      // Récupérer la liste détaillée des étudiants
      const studentDetails = await InstructorAnalyticsService.getStudentDetails(
        tenant.id,
        instructor.id
      );

      setStudents(studentDetails);

      // Mettre à jour les statistiques globales à partir des données détaillées
      if (studentDetails.length > 0) {
        const totalStudents = studentDetails.length;
        const activeStudents = studentDetails.filter(s => s.status === 'active').length;
        const completedCourses = studentDetails.reduce((sum, s) => sum + s.completed_courses, 0);
        const totalProgress = studentDetails.reduce((sum, s) => sum + s.total_progress, 0);
        const averageProgress = totalStudents > 0 ? Math.round(totalProgress / totalStudents) : 0;

        setStudentsStats({
          totalStudents,
          activeStudents,
          completedCourses,
          averageProgress,
        });
      } else {
        setStudentsStats({
          totalStudents: 0,
          activeStudents: 0,
          completedCourses: 0,
          averageProgress: 0,
        });
      }

    } catch (error) {
      console.error('Erreur lors du chargement des étudiants:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des étudiants',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadInstructorCourses = async () => {
    try {
      if (!tenant?.id || !instructor?.id) {
        return;
      }

      const coursesData = await getAvailableCourses({
        tenantId: tenant.id,
        instructorId: instructor.id,
        limit: 50
      });
      
      // Mettre à jour la liste des cours pour le filtre
      if (coursesData.courses) {
        setInstructorCourses(coursesData.courses.map(course => ({
          id: course.id,
          title: course.title
        })));
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    const matchesCourse = courseFilter === 'all' || 
                         student.courses.some(course => course.course_id === courseFilter);
    return matchesSearch && matchesStatus && matchesCourse;
  });

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'enrollment_date':
        return new Date(b.enrollment_date).getTime() - new Date(a.enrollment_date).getTime();
      case 'progress':
        return b.total_progress - a.total_progress;
      case 'grade':
        return b.average_grade - a.average_grade;
      default:
        return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime();
    }
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="outline" className="border-emerald-600/60 text-emerald-200 bg-emerald-900/20 shadow-[0_0_12px_rgba(16,185,129,0.15)]">Actif</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="border-amber-600/50 text-amber-200 bg-amber-900/20 shadow-[0_0_12px_rgba(245,158,11,0.12)]">Inactif</Badge>;
      case 'completed':
        return <Badge variant="outline" className="border-indigo-600/60 text-indigo-200 bg-indigo-900/20 shadow-[0_0_12px_rgba(99,102,241,0.15)]">Terminé</Badge>;
      default:
        return <Badge variant="outline" className="border-gray-700 text-gray-300 bg-gray-900/20">{status}</Badge>;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  const formatRelativeTime = (dateString: string) => {
    try {
      const d = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
    } catch {
      return dateString;
    }
  };

  const handleSendMessage = (studentId: string) => {
    navigate(`/instructor/messages?student=${studentId}`);
  };

  const handleViewProgress = (studentId: string) => {
    navigate(`/instructor/students/${studentId}/progress`);
  };

  if (loading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">


        {/* En-tête avec titre et résumé */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Mes Étudiants</h1>
              <p className="text-gray-400">Gérez et suivez les progrès de tous vos étudiants inscrits.</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="h-4 w-4 text-gray-400" />
              <span>Dernière mise à jour : {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>

        {/* Statistiques principales - Layout amélioré */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{studentsStats.totalStudents}</p>
                  <p className="text-sm font-medium text-gray-300">Total étudiants</p>
                </div>
                <div className="bg-blue-900/30 border border-blue-700/30 p-3 rounded-full">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{studentsStats.activeStudents}</p>
                  <p className="text-sm font-medium text-gray-300">Étudiants actifs</p>
                </div>
                <div className="bg-green-900/30 border border-green-700/30 p-3 rounded-full">
                  <UserCheck className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{studentsStats.completedCourses}</p>
                  <p className="text-sm font-medium text-gray-300">Cours terminés</p>
                </div>
                <div className="bg-amber-900/30 border border-amber-700/30 p-3 rounded-full">
                  <Award className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{studentsStats.averageProgress}%</p>
                  <p className="text-sm font-medium text-gray-300">Progression moyenne</p>
                </div>
                <div className="bg-purple-900/30 border border-purple-700/30 p-3 rounded-full">
                  <TrendingUp className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{instructorCourses.length}</p>
                  <p className="text-sm font-medium text-gray-300">Total des cours</p>
                </div>
                <div className="bg-indigo-900/30 border border-indigo-700/30 p-3 rounded-full">
                  <BookOpen className="h-6 w-6 text-indigo-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {instructorCourses.length > 0
                      ? Math.round((studentsStats.totalStudents / instructorCourses.length) * 10) / 10
                      : 0}
                  </p>
                  <p className="text-sm font-medium text-gray-300">Moy. étudiants/cours</p>
                </div>
                <div className="bg-teal-900/30 border border-teal-700/30 p-3 rounded-full">
                  <Users className="h-6 w-6 text-teal-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section des filtres avec design amélioré */}
        <Card className="mb-8 shadow-lg bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md hover:shadow-xl transition-all">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg font-semibold text-white">Filtres et Recherche</CardTitle>
                <p className="text-sm text-gray-300 mt-1">Affinez votre vue des étudiants</p>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-300 flex-wrap sm:justify-end">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-300" />
                  <span>{filteredStudents.length} résultat{filteredStudents.length > 1 ? 's' : ''}</span>
                </div>
                {searchTerm && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
                    Recherche: <span className="text-gray-200">"{searchTerm}"</span>
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
                  Statut: <span className="text-gray-200">{STATUS_LABELS_FR[statusFilter] ?? statusFilter}</span>
                </span>
                <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
                  Cours: <span className="text-gray-200">{courseFilter === 'all' ? 'Tous les cours' : (instructorCourses.find(c => c.id === courseFilter)?.title || courseFilter)}</span>
                </span>
                <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
                  Tri: <span className="text-gray-200">{SORT_LABELS_FR[sortBy] ?? sortBy}</span>
                </span>
                {(searchTerm || statusFilter !== 'all' || courseFilter !== 'all') && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setSearchTerm(''); setStatusFilter('all'); setCourseFilter('all'); }}
                    className="ml-1 border-gray-700 text-gray-200 hover:bg-gray-800/50"
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Ligne unique avec 4 colonnes sur desktop */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Recherche</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Nom, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-gray-900/40 border border-gray-700 text-gray-200 hover:bg-gray-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300">
                    <span className="truncate">{STATUS_LABELS_FR[statusFilter] ?? 'Statut'}</span>
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border border-gray-700 text-gray-200">
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="inactive">Inactif</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Cours</label>
                <Select value={courseFilter} onValueChange={setCourseFilter}>
                  <SelectTrigger className="bg-gray-900/40 border border-gray-700 text-gray-200 hover:bg-gray-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300">
                    <span className="truncate">{courseFilter === 'all' ? 'Tous les cours' : (instructorCourses.find(c => c.id === courseFilter)?.title || courseFilter)}</span>
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border border-gray-700 text-gray-200">
                    <SelectItem value="all">Tous les cours</SelectItem>
                    {instructorCourses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Trier par</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="bg-gray-900/40 border border-gray-700 text-gray-200 hover:bg-gray-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300">
                    <span className="truncate">{SORT_LABELS_FR[sortBy] ?? 'Trier par'}</span>
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border border-gray-700 text-gray-200">
                    <SelectItem value="last_activity">Dernière activité</SelectItem>
                    <SelectItem value="name">Nom</SelectItem>
                    <SelectItem value="enrollment_date">Date d'inscription</SelectItem>
                    <SelectItem value="progress">Progression</SelectItem>
                    <SelectItem value="grade">Note moyenne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ligne des boutons de vue */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4 mt-6 pt-4 border-t border-gray-700/50">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className={`w-full sm:w-auto flex items-center gap-2 ${viewMode === 'table' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white border-0' : 'border border-gray-700 text-gray-300 hover:bg-gray-800/40'}`}
                >
                  <List className="h-4 w-4" />
                  Tableau
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className={`w-full sm:w-auto flex items-center gap-2 ${viewMode === 'cards' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white border-0' : 'border border-gray-700 text-gray-300 hover:bg-gray-800/40'}`}
                >
                  <Grid className="h-4 w-4" />
                  Cartes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Liste des étudiants */}
        {viewMode === 'cards' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedStudents.map((student) => (
              <Card key={student.id} className="relative overflow-hidden hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/40 rounded-2xl backdrop-blur-md hover:ring-1 hover:ring-indigo-500/30">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12 ring-1 ring-gray-700/60">
                        <AvatarImage src={student.avatar_url || undefined} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-900 to-indigo-900 text-blue-200">
                          {student.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-white">{student.name}</h3>
                        <p className="text-sm text-gray-300">{student.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(student.status)}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-gray-300 hover:bg-gray-800/60">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-gray-800 border border-gray-700 text-gray-200">
                          <DropdownMenuItem onClick={() => handleViewProgress(student.id)} className="hover:bg-gray-700">
                            <Eye className="mr-2 h-4 w-4" />
                            Voir les détails
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSendMessage(student.id)} className="hover:bg-gray-700">
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Envoyer un message
                          </DropdownMenuItem>
                          <DropdownMenuItem className="hover:bg-gray-700">
                            <Mail className="mr-2 h-4 w-4" />
                            Envoyer un email
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-300">Cours inscrits</p>
                      <p className="font-semibold text-gray-200">{student.total_courses}</p>
                    </div>
                    <div>
                      <p className="text-gray-300">Cours terminés</p>
                      <p className="font-semibold text-gray-200">{student.completed_courses}</p>
                    </div>
                    <div>
                      <p className="text-gray-300">Temps d'étude</p>
                      <p className="font-semibold text-gray-200">{formatTime(student.total_time_spent)}</p>
                    </div>
                    <div>
                      <p className="text-gray-300">Note moyenne</p>
                      <p className="font-semibold text-gray-200">
                        {student.average_grade > 0 ? `${student.average_grade}%` : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-300">Progression globale</span>
                      <span className="font-semibold text-gray-200">{student.total_progress}%</span>
                    </div>
                    <Progress value={student.total_progress} className="h-2" />
                  </div>
                  
                  <div className="text-xs text-gray-300">
                    Dernière activité: {formatRelativeTime(student.last_activity)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="shadow-md bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-white">Liste des Étudiants</CardTitle>
                  <p className="text-sm text-gray-400 mt-1">Vue détaillée de tous vos étudiants</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Users className="h-4 w-4" />
                  <span>{sortedStudents.length} étudiant{sortedStudents.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-800/50">
                    <TableHead className="font-semibold text-gray-300">Étudiant</TableHead>
                    <TableHead className="font-semibold text-gray-300">Statut</TableHead>
                    <TableHead className="font-semibold text-gray-300">Cours</TableHead>
                    <TableHead className="font-semibold text-gray-300">Progression</TableHead>
                    <TableHead className="font-semibold text-gray-300">Note moy.</TableHead>
                    <TableHead className="font-semibold text-gray-300">Temps d'étude</TableHead>
                    <TableHead className="font-semibold text-gray-300">Dernière activité</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedStudents.map((student, index) => (
                    <TableRow key={student.id} className={`hover:bg-gray-800/40 transition-colors ${index % 2 === 0 ? 'bg-gray-900/20' : 'bg-gray-900/10'}`}>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 ring-1 ring-gray-700/60">
                            <AvatarImage src={student.avatar_url || undefined} />
                            <AvatarFallback className="text-sm font-medium bg-gradient-to-br from-blue-900 to-indigo-900 text-blue-200">
                              {student.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-white">{student.name}</p>
                            <p className="text-sm text-gray-300 flex items-center gap-1">
                              <Mail className="h-3 w-3 text-gray-400" />
                              {student.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">{getStatusBadge(student.status)}</TableCell>
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-blue-400" />
                            <span className="font-medium text-sm text-white">{student.total_courses} inscrits</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Award className="h-4 w-4 text-green-400" />
                            <span className="text-sm text-gray-300">{student.completed_courses} terminés</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="w-24">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-300">Progrès</span>
                            <span className="font-semibold text-white">{student.total_progress}%</span>
                          </div>
                          <Progress value={student.total_progress} className="h-2" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            student.average_grade >= 80 ? 'bg-green-500' :
                            student.average_grade >= 60 ? 'bg-yellow-500' :
                            student.average_grade > 0 ? 'bg-red-500' : 'bg-gray-300'
                          }`} />
                          <span className="font-medium text-gray-200">
                            {student.average_grade > 0 ? `${student.average_grade}%` : 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-200">{formatTime(student.total_time_spent)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="text-sm">
                          <span className="text-gray-300">{formatRelativeTime(student.last_activity)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-800/60 text-gray-300">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 bg-gray-800 border border-gray-700 text-gray-200">
                            <DropdownMenuItem onClick={() => handleViewProgress(student.id)} className="cursor-pointer hover:bg-gray-700">
                              <Eye className="mr-2 h-4 w-4" />
                              Voir les détails
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendMessage(student.id)} className="cursor-pointer hover:bg-gray-700">
                              <MessageSquare className="mr-2 h-4 w-4" />
                              Envoyer un message
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* État vide amélioré */}
        {sortedStudents.length === 0 && (
          <Card className="mt-8 bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl">
            <CardContent className="py-16">
              <div className="text-center max-w-md mx-auto">
                <div className="bg-gray-800 border border-gray-700 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Users className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {searchTerm || statusFilter !== 'all' || courseFilter !== 'all'
                    ? 'Aucun étudiant trouvé'
                    : (studentsStats.totalStudents > 0
                        ? 'Données des étudiants indisponibles'
                        : 'Aucun étudiant inscrit')}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {searchTerm || statusFilter !== 'all' || courseFilter !== 'all'
                    ? 'Aucun étudiant ne correspond à vos critères de recherche actuels. Essayez de modifier vos filtres pour voir plus de résultats.'
                    : (studentsStats.totalStudents > 0
                        ? 'Des étudiants sont inscrits à vos cours, mais la liste détaillée n\'est pas encore disponible. Les données seront bientôt chargées.'
                        : 'Vous n\'avez pas encore d\'étudiants inscrits à vos cours. Une fois que des apprenants rejoindront vos formations, vous pourrez suivre leurs progrès ici.')}
                </p>
                {(searchTerm || statusFilter !== 'all' || courseFilter !== 'all') && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setCourseFilter('all');
                    }}
                    className="flex items-center gap-2 border-gray-700 text-gray-200 hover:bg-gray-800/40"
                  >
                    <FilterIcon className="h-4 w-4" />
                    Réinitialiser les filtres
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </InstructorLayout>
  );
};

export default InstructorStudentsPage;
